<template>
  <div v-if="show" class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h2>Add Beneficiaries to Coupon</h2>
        <button @click="closeModal" class="close-btn">&times;</button>
      </div>
      
      <div class="modal-body">
        <!-- Search and Filters -->
        <div class="search-section">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search users by name or email..."
            class="search-input"
          />
          <div class="filter-controls">
            <button
              @click="selectAll"
              :disabled="filteredUsers.length === 0"
              class="btn-secondary"
            >
              {{ allSelected ? 'Deselect All' : 'Select All' }}
            </button>
            <span class="selection-count">
              {{ selectedUsers.length }} of {{ filteredUsers.length }} selected
            </span>
          </div>
        </div>

        <!-- Users List -->
        <div class="users-container">
          <div v-if="isLoadingUsers" class="loading-state">
            Loading users...
          </div>
          
          <div v-else-if="filteredUsers.length === 0" class="empty-state">
            <p>{{ searchQuery ? 'No users found matching your search.' : 'No users available.' }}</p>
          </div>
          
          <div v-else class="users-grid">
            <div
              v-for="user in paginatedUsers"
              :key="user.id"
              :class="['user-card', { selected: isUserSelected(user.id) }]"
              @click="toggleUser(user.id)"
            >
              <div class="user-checkbox">
                <input
                  type="checkbox"
                  :checked="isUserSelected(user.id)"
                  @change="toggleUser(user.id)"
                  @click.stop
                />
              </div>
              
              <div class="user-info">
                <div class="user-avatar">
                  <img :src="generateAvatarUrl(user.name)" :alt="user.name" />
                </div>
                <div class="user-details">
                  <div class="user-name">{{ user.name }}</div>
                  <div class="user-email">{{ user.email }}</div>
                  <div class="user-meta">
                    <span class="user-type">{{ user.accountType || 'Individual' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination for users -->
          <div v-if="filteredUsers.length > usersPerPage" class="modal-pagination">
            <button
              @click="userCurrentPage--"
              :disabled="userCurrentPage === 1"
              class="pagination-btn"
            >
              Previous
            </button>
            <span class="pagination-info">
              Page {{ userCurrentPage }} of {{ totalUserPages }}
            </span>
            <button
              @click="userCurrentPage++"
              :disabled="userCurrentPage === totalUserPages"
              class="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="closeModal" class="btn-secondary" :disabled="isSubmitting">
          Cancel
        </button>
        <button
          @click="addSelectedBeneficiaries"
          :disabled="selectedUsers.length === 0 || isSubmitting"
          class="btn-primary"
        >
          {{ isSubmitting ? 'Adding...' : `Add ${selectedUsers.length} Beneficiaries` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import rewardMgtService from '../../../../services/rewardMgt-service';
import { useToast } from 'vue-toastification';

export default {
  name: 'AddBeneficiaryModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    couponId: {
      type: String,
      required: true
    }
  },
  emits: ['close', 'beneficiaries-added'],
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      searchQuery: '',
      selectedUsers: [],
      availableUsers: [],
      isLoadingUsers: false,
      isSubmitting: false,
      userCurrentPage: 1,
      usersPerPage: 12
    };
  },
  
  computed: {
    filteredUsers() {
      if (!this.searchQuery) return this.availableUsers;
      
      const query = this.searchQuery.toLowerCase();
      return this.availableUsers.filter(user =>
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
      );
    },
    
    paginatedUsers() {
      const start = (this.userCurrentPage - 1) * this.usersPerPage;
      const end = start + this.usersPerPage;
      return this.filteredUsers.slice(start, end);
    },
    
    totalUserPages() {
      return Math.ceil(this.filteredUsers.length / this.usersPerPage);
    },
    
    allSelected() {
      return this.filteredUsers.length > 0 && 
             this.filteredUsers.every(user => this.isUserSelected(user.id));
    }
  },
  
  watch: {
    show(newVal) {
      if (newVal) {
        this.loadAvailableUsers();
        this.resetModal();
      }
    },
    
    searchQuery() {
      this.userCurrentPage = 1;
    }
  },
  
  methods: {
    async loadAvailableUsers() {
      this.isLoadingUsers = true;
      try {
        // Load all customers/users who can be added as beneficiaries
        const response = await rewardMgtService.getAllCustomers();
        
        if (response.status === 200 && response.data) {
          this.availableUsers = response.data || [];
        } else {
          this.availableUsers = [];
        }
        
        // You might want to filter out users who are already beneficiaries
        // This would require getting current beneficiaries and filtering them out
        
      } catch (error) {
        console.error('Error loading users:', error);
        this.toast.error('Failed to load available users');
        this.availableUsers = [];
      } finally {
        this.isLoadingUsers = false;
      }
    },
    
    resetModal() {
      this.searchQuery = '';
      this.selectedUsers = [];
      this.userCurrentPage = 1;
    },
    
    closeModal() {
      this.$emit('close');
    },
    
    toggleUser(userId) {
      const index = this.selectedUsers.indexOf(userId);
      if (index > -1) {
        this.selectedUsers.splice(index, 1);
      } else {
        this.selectedUsers.push(userId);
      }
    },
    
    isUserSelected(userId) {
      return this.selectedUsers.includes(userId);
    },
    
    selectAll() {
      if (this.allSelected) {
        this.selectedUsers = [];
      } else {
        this.selectedUsers = [...this.filteredUsers.map(user => user.id)];
      }
    },
    
    generateAvatarUrl(name) {
      if (!name) return 'https://ui-avatars.com/api/?name=User&background=6b7280&color=fff';
      const colors = ['6366f1', '10b981', '8b5cf6', 'f59e0b', 'ef4444', '06b6d4'];
      const colorIndex = name.length % colors.length;
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${colors[colorIndex]}&color=fff`;
    },
    
    async addSelectedBeneficiaries() {
      if (this.selectedUsers.length === 0) {
        this.toast.warning('Please select at least one user');
        return;
      }
      
      this.isSubmitting = true;
      try {
        // Call API to add beneficiaries to the coupon
        // Your existing service expects the data in this format based on your current service
        const response = await rewardMgtService.applyCouponToCustomers(this.couponId, this.selectedUsers);
        
        if (response.status === 200) {
          // Get the selected user objects to emit back to parent
          const addedUsers = this.availableUsers
            .filter(user => this.selectedUsers.includes(user.id))
            .map(user => ({
              id: `${user.id}-${Date.now()}`, // Temporary ID until we get the real one from API
              couponId: this.couponId,
              userId: user.id,
              userName: user.name,
              userEmail: user.email,
              assignedDate: new Date().toISOString(),
              isUsed: false,
              usedDate: null,
              amountSpent: null
            }));
          
          this.$emit('beneficiaries-added', addedUsers);
          this.toast.success(`Successfully added ${this.selectedUsers.length} beneficiaries`);
          this.closeModal();
        } else {
          this.toast.error('Failed to add beneficiaries');
        }
        
      } catch (error) {
        console.error('Error adding beneficiaries:', error);
        this.toast.error('Failed to add beneficiaries. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  line-height: 1;
}

.close-btn:hover {
  color: #374151;
}

.modal-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.search-section {
  padding: 24px 24px 0 24px;
}

.search-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  margin-bottom: 16px;
}

.search-input:focus {
  border-color: #3b82f6;
}

.filter-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.selection-count {
  font-size: 14px;
  color: #6b7280;
}

.users-container {
  flex: 1;
  overflow: auto;
  padding: 0 24px 24px 24px;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6b7280;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6b7280;
  text-align: center;
}

.users-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}

.user-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 2px solid #f3f4f6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.user-card:hover {
  border-color: #e5e7eb;
  background: #f9fafb;
}

.user-card.selected {
  border-color: #3b82f6;
  background: #eff6ff;
}

.user-checkbox {
  flex-shrink: 0;
}

.user-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-details {
  min-width: 0;
  flex: 1;
}

.user-name {
  font-weight: 600;
  color: #111827;
  margin-bottom: 2px;
}

.user-email {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
}

.user-meta {
  font-size: 11px;
  color: #9ca3af;
}

.user-type {
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
}

.modal-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 14px;
  color: #6b7280;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #e5e7eb;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border: none;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover:not(:disabled) {
  background: #f9fafb;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    max-height: 95vh;
  }
  
  .users-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-header,
  .modal-footer {
    padding: 16px;
  }
  
  .search-section {
    padding: 16px 16px 0 16px;
  }
  
  .users-container {
    padding: 0 16px 16px 16px;
  }
  
  .filter-controls {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }
  
  .modal-footer {
    flex-direction: column-reverse;
  }
  
  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>